import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';

/**
 * Centralized PDF operations utility for form handling and browser compatibility
 * Provides consistent PDF flattening and cross-browser support
 */
export class PDFOperations {
  /**
   * Save specific pages from a PDF document with form flattening
   * @param {PDFDocument} pdfDoc - Source PDF document
   * @param {number[]} pageIndices - Array of page indices to keep
   * @returns {Promise<Uint8Array>} - Flattened PDF bytes
   */
  static async saveSpecificPages(pdfDoc, pageIndices) {
    const pdfBytes = await pdfDoc.save();
    const newPdfDoc = await PDFDocument.load(pdfBytes);

    const form = newPdfDoc.getForm();
    // Try to flatten the form (makes fields non-editable but ensures visibility across all browsers)
    try {
      form.flatten();
    } catch (error) {
      console.error('PDF form flatten error in saveSpecificPages:', error);
      // Continue with unflattened version
    }

    const pageIndexToKeep = pageIndices[0];
    const totalPages = newPdfDoc.getPageCount();

    // Remove pages from the end to the beginning, except the one we want to keep
    for (let i = totalPages - 1; i >= 0; i--) {
      if (i !== pageIndexToKeep) {
        newPdfDoc.removePage(i);
      }
    }
    
    // Serialize the new PDF document to bytes
    const newPdfBytes = await newPdfDoc.save();
    return newPdfBytes;
  }

  /**
   * Create a flattened PDF for download
   * @param {PDFDocument} pdfDoc - Source PDF document
   * @returns {Promise<Uint8Array>} - Flattened PDF bytes
   */
  static async createFlattenedPDF(pdfDoc) {
    const pdfBytes = await pdfDoc.save();
    const flattenedDoc = await PDFDocument.load(pdfBytes);

    const form = flattenedDoc.getForm();
    // Try to flatten the form
    try {
      form.flatten();
    } catch (error) {
      console.error('PDF form flatten error in createFlattenedPDF:', error);
      // Continue with unflattened version
    }
    
    // Serialize the new PDF document to bytes
    const newPdfBytes = await flattenedDoc.save();
    return newPdfBytes;
  }

  /**
   * Set all form fields read-only or editable
   * @param {PDFForm} pdfForm - PDF form object
   * @param {boolean} isReadOnly - Whether fields should be read-only
   */
  static setAllFieldsReadOnly(pdfForm, isReadOnly) {
    if (!pdfForm) return;

    const fields = pdfForm.getFields();
    fields.forEach(field => {
      if (field.constructor.name === 'PDFTextField' || field.constructor.name === 'PDFCheckBox') {
        if (isReadOnly) {
          field.enableReadOnly();
        } else {
          field.disableReadOnly();
        }
      }
    });
  }

  /**
   * Create blob URL from PDF bytes
   * @param {Uint8Array} pdfBytes - PDF document bytes
   * @returns {string} - Blob URL
   */
  static createBlobUrl(pdfBytes) {
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  }

  /**
   * Download PDF with specified filename
   * @param {Uint8Array} pdfBytes - PDF document bytes  
   * @param {string} filename - Download filename
   */
  static async downloadPDF(pdfBytes, filename) {
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    await saveAs(blob, filename);
  }

  /**
   * Cross-browser PDF printing with Safari compatibility
   * @param {string} url - PDF blob URL
   */
  static printPdfFromUrl(url) {
    // 1 · Headless iframe (can't be display:none for Safari)
    const frame = document.createElement('iframe');
    Object.assign(frame.style, {
      position: 'fixed', right: 0, bottom: 0,
      width: '0', height: '0', border: 'none',
      visibility: 'hidden'
    });
    frame.src = url;

    // 2 · Single cleanup routine
    const cleanUp = () => {
      frame.remove();
      URL.revokeObjectURL(url);
      window.removeEventListener('afterprint', cleanUp);
      mq.removeEventListener?.('change', mmHandler);   // tidy the fallback, too
    };

    // 3 · Works in every browser (event is fired on *our* window)
    window.addEventListener('afterprint', cleanUp, { once: true });

    // 3 bis · Chrome 88-96 bug-fix: media-query fallback
    const mq = window.matchMedia('print');
    const mmHandler = e => { if (!e.matches) cleanUp(); };
    (mq.addEventListener ?? mq.addListener).call(mq, 'change', mmHandler);

    // 4 · Wait for the PDF to render, then print
    frame.onload = () => {
      // Short pause cures "first page blank" in Safari/WebKit
      setTimeout(() => {
        frame.contentWindow.focus();     // optional but helps old Edge
        frame.contentWindow.print();     // safe in Firefox ≥ 75
      }, 50);
    };

    document.body.appendChild(frame);
  }

  /**
   * Generate filename from form data
   * @param {Object} formData - Form data containing firstname/lastname
   * @param {string} prefix - Filename prefix
   * @returns {string} - Generated filename
   */
  static generateFilename(formData, prefix = "antrag_zuschuss") {
    const name = formData.firstname.substring(0, 2) + formData.lastname.substring(0, 2);
    return `${prefix}_${name}.pdf`;
  }

  /**
   * Complete download workflow with form state management
   * @param {PDFDocument} pdfDoc - Source PDF document
   * @param {PDFForm} pdfForm - PDF form object
   * @param {Object} formData - Form data for filename generation
   */
  static async downloadFormWorkflow(pdfDoc, pdfForm, formData) {
    if (!pdfDoc) return;

    this.setAllFieldsReadOnly(pdfForm, false);
    const pdfBytes = await this.createFlattenedPDF(pdfDoc);
    const filename = this.generateFilename(formData);
    await this.downloadPDF(pdfBytes, filename);
    this.setAllFieldsReadOnly(pdfForm, true);
  }

  /**
   * Complete print workflow with form state management
   * @param {PDFDocument} pdfDoc - Source PDF document
   * @param {PDFForm} pdfForm - PDF form object
   */
  static async printFormWorkflow(pdfDoc, pdfForm) {
    if (!pdfDoc) return;

    this.setAllFieldsReadOnly(pdfForm, false);
    const pdfBytes = await this.createFlattenedPDF(pdfDoc);
    this.setAllFieldsReadOnly(pdfForm, true);

    const blobUrl = this.createBlobUrl(pdfBytes);
    this.printPdfFromUrl(blobUrl);
  }

  /**
   * Render specific page for display workflow
   * @param {PDFDocument} pdfDoc - Source PDF document
   * @param {number} currentTab - Current page index to render
   * @returns {Promise<string>} - Blob URL for display
   */
  static async renderPageWorkflow(pdfDoc, currentTab) {
    if (!pdfDoc) return null;

    const pdfBytes = await this.saveSpecificPages(pdfDoc, [currentTab]);
    return this.createBlobUrl(pdfBytes);
  }
}